import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { friendlyError } from "../lib/errors";

type Tab = "password" | "code";
type PasswordMode = "signin" | "signup" | "forgot";

// Block the bottom of the SecLists "10-million-password-list" — anything an
// attacker tries in the first second of a spray. Not a substitute for Supabase's
// HaveIBeenPwned check (enable in dashboard) but stops the laziest attacks.
const TOP_WEAK_PASSWORDS = new Set([
  "password", "password1", "password12", "password123", "password1234",
  "qwerty123", "qwerty1234", "12345678", "123456789", "1234567890",
  "letmein123", "iloveyou1", "admin1234", "welcome123", "monkey1234",
  "abc1234567", "111111111", "passw0rd1", "p@ssw0rd1", "trustno1234"
]);

export default function SignIn() {
  const { signInWithPassword, signUpWithPassword, signInWithMagicLink, verifyEmailOtp, sendPasswordResetEmail, authLinkError, clearAuthLinkError } = useAuth();
  const [tab, setTab] = useState<Tab>("password");
  const [pwMode, setPwMode] = useState<PasswordMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Invite code — OPTIONAL. Whitelisted emails (added by the founder via
  // /admin or SQL) sign up without one. Codes are for the "share this with
  // your friends" path where a single redemption auto-whitelists the user.
  // Auto-filled from ?invite=TALLY-XXXX URL param. We deliberately use
  // `invite=` not `code=` to avoid colliding with Supabase's PKCE flow
  // which uses `?code=...` for auth-link exchanges.
  const [inviteCode, setInviteCode] = useState("");
  /** Hidden by default — show it only when the user explicitly clicks
   *  "I have an invite code" OR when the URL pre-fills one. Keeps the
   *  signup form clean for whitelisted users (no false "required" friction). */
  const [showInviteField, setShowInviteField] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fromUrl = new URLSearchParams(window.location.search).get("invite");
    if (fromUrl) {
      setInviteCode(fromUrl.toUpperCase());
      setShowInviteField(true);
      // Default to the signup tab — most people landing with ?invite= are
      // brand-new users, not existing ones.
      setPwMode("signup");
    }
  }, []);

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (pwMode === "signup") {
        if (password.length < 10) {
          setError("Password must be at least 10 characters.");
          return;
        }
        // Light client-side check against the most common weak passwords.
        // Supabase HaveIBeenPwned check (if enabled in dashboard) catches the rest.
        if (TOP_WEAK_PASSWORDS.has(password.toLowerCase())) {
          setError("That password is too common — pick something less guessable.");
          return;
        }
        // Beta gate: if the user provided an invite code, redeem it before
        // attempting signup. The redeem endpoint adds them to beta_whitelist
        // so the Postgres trigger lets the signup through. If no code, signup
        // still goes ahead — it'll succeed only for emails James pre-added
        // to the whitelist directly (e.g. approved waitlist entries).
        if (inviteCode.trim()) {
          const res = await fetch("/api/redeem-invite", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email: email.trim(), code: inviteCode.trim() })
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            setError(body?.error || "Invite code didn't work — double-check it?");
            return;
          }
        }
        try {
          await signUpWithPassword(email, password);
        } catch (err) {
          // Map the Postgres trigger's BETA_LOCKED error to actionable copy.
          // Supabase Auth strips Postgres error messages for security, so the
          // client sees a generic "Database error saving new user" instead of
          // our trigger's friendly message — we detect both. Since the gate
          // trigger is the only one on auth.users, this generic message is
          // safe to map directly to the invite-only explanation.
          const raw = (err as Error)?.message ?? String(err);
          const beta =
            raw.includes("BETA_LOCKED") ||
            raw.toLowerCase().includes("database error saving new user");
          if (beta) {
            setError(
              "Tallyplate is in invite-only beta. Enter an invite code above, or " +
              "join the waitlist at tallyplate.app and we'll send you one in the next batch."
            );
            return;
          }
          throw err;
        }
      } else {
        await signInWithPassword(email, password);
      }
      // session change handled by AuthProvider → AuthGate swaps views
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitForgotPassword(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    setError(null);
    try {
      // Add a 20s timeout — same protection as the magic-link flow, in case
      // SMTP misbehaves.
      await Promise.race([
        sendPasswordResetEmail(email),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Timed out after 20s. Check your spam folder — the email may still arrive.")),
            20_000
          )
        )
      ]);
      setResetSent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Wrap in a 20s race so the button doesn't hang on "Sending…" forever
      // when the auth API or SMTP backend silently stalls. Without this the
      // button stays stuck and the user has no signal something went wrong.
      await Promise.race([
        signInWithMagicLink(email),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("Timed out after 20s. Check your spam folder — the email may still arrive. Or try the Password tab.")),
            20_000
          )
        )
      ]);
      setCodeSent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await verifyEmailOtp(email, code);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col justify-center max-w-md mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        {/* Brand mark — echoes the calorie ring on the dashboard */}
        <svg width="48" height="48" viewBox="0 0 48 48" className="flex-shrink-0">
          <defs>
            <linearGradient id="brand-em" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="48" y2="48">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#6ee7b7" />
            </linearGradient>
          </defs>
          <circle cx="24" cy="24" r="19" fill="none" stroke="#1e293b" strokeWidth="6" />
          <circle
            cx="24"
            cy="24"
            r="19"
            fill="none"
            stroke="url(#brand-em)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={Math.PI * 2 * 19}
            strokeDashoffset={Math.PI * 2 * 19 * 0.3}
            transform="rotate(-90 24 24)"
          />
        </svg>
        <div>
          <h1 className="text-3xl font-bold text-slate-100 tracking-tight leading-none">Tallyplate</h1>
          <p className="text-slate-400 mt-1 text-sm">Snap your meal. Skip the logging.</p>
        </div>
      </div>

      {/* Auth-link error banner — surfaces expired magic links / used OTPs /
          malformed links so the user knows WHY they're staring at the sign-in
          screen after clicking an email link. */}
      {authLinkError && (
        <div className="p-3 rounded-lg bg-amber-950 border border-amber-800 text-amber-200 text-sm flex items-start gap-3" role="alert">
          <div className="flex-1">{authLinkError}</div>
          <button
            type="button"
            onClick={clearAuthLinkError}
            className="text-amber-300 hover:text-amber-100 text-xs font-medium"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
        <button
          onClick={() => {
            setTab("password");
            setError(null);
          }}
          className={`flex-1 py-2 rounded text-sm font-medium ${
            tab === "password" ? "bg-slate-800 text-emerald-400" : "text-slate-400"
          }`}
        >
          Password
        </button>
        <button
          onClick={() => {
            setTab("code");
            setError(null);
          }}
          className={`flex-1 py-2 rounded text-sm font-medium ${
            tab === "code" ? "bg-slate-800 text-emerald-400" : "text-slate-400"
          }`}
        >
          Email code
        </button>
      </div>

      {tab === "password" && pwMode === "forgot" && (
        resetSent ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-emerald-950 border border-emerald-800 text-emerald-100 text-sm">
              Reset link sent to <span className="font-mono">{email}</span>. Check your inbox + spam folder. The link signs you in temporarily so you can set a new password.
            </div>
            <button
              type="button"
              onClick={() => {
                setPwMode("signin");
                setResetSent(false);
                setError(null);
              }}
              className="text-sm text-slate-400 underline w-full text-center"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submitForgotPassword} className="space-y-3">
            <p className="text-sm text-slate-400">
              We'll email you a link. Clicking it signs you in temporarily so you can set a new password.
            </p>
            <label className="block">
              <span className="text-sm text-slate-400">Email</span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
              />
            </label>
            <button
              type="submit"
              disabled={busy || !email}
              className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send reset link"}
            </button>
            <button
              type="button"
              onClick={() => {
                setPwMode("signin");
                setError(null);
              }}
              className="text-sm text-slate-400 underline w-full text-center"
            >
              Back to sign in
            </button>
            {error && (
              <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
                {error}
              </div>
            )}
          </form>
        )
      )}

      {tab === "password" && pwMode !== "forgot" && (
        <form onSubmit={submitPassword} className="space-y-3">
          <label className="block">
            <span className="text-sm text-slate-400">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </label>

          <label className="block">
            <span className="text-sm text-slate-400 flex justify-between">
              <span>
                Password {pwMode === "signup" && <span className="text-slate-500">(min 10)</span>}
              </span>
              {pwMode === "signin" && (
                <button
                  type="button"
                  onClick={() => {
                    setPwMode("forgot");
                    setError(null);
                  }}
                  className="text-emerald-400 hover:text-emerald-300 underline text-xs"
                >
                  Forgot?
                </button>
              )}
            </span>
            <input
              type="password"
              required
              minLength={pwMode === "signup" ? 10 : undefined}
              autoComplete={pwMode === "signup" ? "new-password" : "current-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </label>

          {/* Invite code — only on signup, and hidden by default. Most
              users land here from a direct whitelist add (no code needed)
              or from an ?invite= URL (auto-fills + auto-shows). Showing
              the field unconditionally led to confusion: people thought
              the code was required even when their email was already
              approved. Now it's behind a toggle.

              When the field IS shown, an empty value is fine — the signup
              flow falls through to the DB whitelist check. The code only
              matters when the user genuinely has one to redeem. */}
          {pwMode === "signup" && !showInviteField && (
            <button
              type="button"
              onClick={() => setShowInviteField(true)}
              className="block text-xs text-slate-400 hover:text-emerald-400 underline-offset-2 hover:underline -mt-1"
            >
              I have an invite code
            </button>
          )}
          {pwMode === "signup" && showInviteField && (
            <label className="block">
              <span className="text-sm text-slate-400">
                Invite code <span className="text-slate-500">(only if you have one)</span>
              </span>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="TALLY-XXXX"
                autoComplete="off"
                spellCheck={false}
                className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 tracking-wider uppercase font-mono"
              />
              <span className="text-[11px] text-slate-500 mt-1 block">
                If your email was added to the beta directly, leave this blank. Otherwise <a href="https://tallyplate.app" className="text-emerald-400 underline">join the waitlist</a> for an invite.
              </span>
            </label>
          )}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            {busy
              ? pwMode === "signup"
                ? "Creating account…"
                : "Signing in…"
              : pwMode === "signup"
                ? "Create account"
                : "Sign in"}
          </button>

          <button
            type="button"
            onClick={() => {
              setPwMode(pwMode === "signin" ? "signup" : "signin");
              setError(null);
            }}
            className="text-sm text-slate-400 underline w-full text-center"
          >
            {pwMode === "signin"
              ? "First time? Create an account"
              : "Already have an account? Sign in"}
          </button>

          {error && (
            <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
              {error}
            </div>
          )}
        </form>
      )}

      {tab === "code" && !codeSent && (
        <form onSubmit={sendCode} className="space-y-3">
          <p className="text-xs text-slate-500">
            For accounts you've already created — we'll email you a 6-digit code that signs you in.
          </p>
          <label className="block">
            <span className="text-sm text-slate-400">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
            />
          </label>
          <button
            type="submit"
            disabled={busy || !email}
            className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            {busy ? "Sending…" : "Send code"}
          </button>
          {error && (
            <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
              {error}
            </div>
          )}
        </form>
      )}

      {tab === "code" && codeSent && (
        <form onSubmit={submitCode} className="space-y-3">
          <div className="p-3 rounded-lg bg-emerald-950 border border-emerald-800 text-emerald-100 text-sm">
            Code sent to <span className="font-mono">{email}</span>.
          </div>
          <label className="block">
            <span className="text-sm text-slate-400">6-digit code</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="mt-1 w-full bg-slate-900 border border-slate-800 rounded-lg p-3 text-slate-100 text-center text-2xl tracking-widest font-mono placeholder:text-slate-600 focus:outline-none focus:border-emerald-500"
            />
          </label>
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="w-full py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Sign in"}
          </button>
          <button
            type="button"
            onClick={() => {
              setCodeSent(false);
              setCode("");
              setError(null);
            }}
            className="text-xs text-slate-400 underline self-start"
          >
            Use a different email
          </button>
          {error && (
            <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
              {error}
            </div>
          )}
        </form>
      )}

      <div className="text-xs text-slate-400">
        First time? Pick Password and tap "Create an account". You'll be signed in immediately.
      </div>

      <div className="text-xs text-slate-500 text-center pt-2">
        By creating an account you agree to our{" "}
        <Link to="/legal/terms" className="text-emerald-400 underline">Terms</Link>
        {" "}and{" "}
        <Link to="/legal" className="text-emerald-400 underline">Privacy Policy</Link>.
      </div>
    </div>
  );
}
